/**
 * Excel export for the harvest monitoring SPB view (one row per SPB).
 */

import * as XLSX from 'xlsx-js-style';
import { trackDownload } from '../../activity';
import { formatDate } from './utils';
import type { SPBEstateRecord } from './types';

const HEADERS = [
    'Tanggal',
    'Estate',
    'Divisi',
    'No. SPB',
    'Driver',
    'No. Kendaraan',
    'Pemuat',
    'Pelangsir',
    'No. Tiket',
    'Janjang BCC',
    'Janjang SPB',
    'Janjang Restan',
    'Berat BCC (kg)',
    'Berat SPB (kg)',
    'Berat Restan (kg)',
];
const FIRST_NUMBER_COL = 9;
const HEADER_ROW = 2;

const thinBorder = (rgb: string) => ({
    top: { style: 'thin', color: { rgb } },
    bottom: { style: 'thin', color: { rgb } },
    left: { style: 'thin', color: { rgb } },
    right: { style: 'thin', color: { rgb } },
});

const headerStyle = {
    font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 12 },
    fill: { fgColor: { rgb: '4A5A33' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: thinBorder('CCCCCC'),
};

export const exportSpbToExcel = (data: SPBEstateRecord[], title: string, filename: string): void => {
    const rows = data.flatMap((estate) =>
        estate.divisions.flatMap((division) =>
            division.rows.map((row) => [
                formatDate(row.transport_date),
                estate.estate_name,
                division.division || row.division,
                row.transport_nbr || '(Belum SPB)',
                row.driver_name || '-',
                row.equipment_nbr || '-',
                row.loader_names || '-',
                row.mover_names || '-',
                row.ticket_no || '-',
                row.bcc_bunch || 0,
                row.spb_bunch || 0,
                row.jjg_restan || 0,
                row.bcc_weight || 0,
                row.spb_weight || 0,
                row.restan_weight || 0,
            ]),
        ),
    );
    if (rows.length === 0) return;

    const worksheet = XLSX.utils.aoa_to_sheet([[title], [], HEADERS, ...rows]);

    worksheet[XLSX.utils.encode_cell({ r: 0, c: 0 })].s = { font: { bold: true, sz: 14 } };
    HEADERS.forEach((_, c) => {
        worksheet[XLSX.utils.encode_cell({ r: HEADER_ROW, c })].s = headerStyle;
    });
    rows.forEach((_, i) => {
        HEADERS.forEach((__, c) => {
            const cell = worksheet[XLSX.utils.encode_cell({ r: HEADER_ROW + 1 + i, c })];
            if (!cell) return;
            const isNumber = c >= FIRST_NUMBER_COL;
            cell.s = {
                font: { sz: 11 },
                alignment: { horizontal: isNumber ? 'right' : 'left', vertical: 'center' },
                border: thinBorder('E2E8F0'),
            };
            if (isNumber) cell.z = '#,##0';
        });
    });

    worksheet['!cols'] = HEADERS.map((h) => ({ wch: Math.max(12, h.length + 4) }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Monitoring SPB');
    XLSX.writeFile(workbook, filename);
    trackDownload('Monitoring Panen SPB', { filename });
};
